import { normalizeAmount } from '@pcrm/shared/money';
import type { InvoiceLine, LineCurrency, ManualInvoiceRequest } from './customer-billing.api';

/**
 * Formularul „Emite factură" din fișa clientului
 * ([#186](https://github.com/bplugaru/efx-pcrm/issues/186)), ca reguli pure: ce se tastează, ce
 * pleacă la server.
 *
 * Serverul validează forma încă o dată, iar regulile fiscale (lungimea denumirii, o reducere mai
 * mare decât factura) sunt ale modulului. Aici e doar ce se poate spune **sub câmp**, înainte de
 * previzualizare, ca operatorul să nu afle dintr-un toast că a scris „2,5,0".
 */

/**
 * Tipul facturii, ales explicit: abonamentul pe una sau mai multe luni (`monthly`) sau echipamente și servicii
 * (`free`). Vezi docs/wiki/domains/invoice-from-customer.md
 */
export type InvoiceKind = 'monthly' | 'free';

/** Cum se numesc tipurile în ecran (fondatorul, #201: „factură liberă" suna prost). */
export const INVOICE_KIND_LABELS: Readonly<Record<InvoiceKind, string>> = {
  monthly: 'Abonament lunar',
  free: 'Echipamente și servicii',
};

/** O linie, cum o editează formularul: șirurile tastate, cu virgulă sau punct. */
export interface LineDraft {
  /** Cheie de randare: liniile se adaugă și se scot din mijloc. */
  readonly key: string;
  readonly description: string;
  readonly unitCode: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly currency: LineCurrency;
  readonly priceIncludesVat: boolean;
  /** Echipament, nu serviciu (#211). Precompletat din tipul pachetului; altfel îl alege omul. */
  readonly isEquipment: boolean;
}

export interface InvoiceFormDraft {
  readonly kind: InvoiceKind;
  /**
   * Lunile facturate, `AAAA-LL`, în ordine — una sau mai multe, oricare, nu neapărat legate
   * (#210). Folosite doar la factura de abonament.
   */
  readonly months: readonly string[];
  /** Data facturii, `AAAA-LL-ZZ`. */
  readonly issueDate: string;
  /** Scadența, `AAAA-LL-ZZ`, sau gol pentru cea obișnuită. */
  readonly dueDate: string;
  readonly lines: readonly LineDraft[];
}

/** Erorile, pe câmp: `lines.2.unitPrice`. */
export type FormErrors = Readonly<Record<string, string>>;

/** Lungimea maximă a denumirii (BT-153, BR-RO-L1024). Modulul o verifică oricum. */
export const LINE_DESCRIPTION_MAX = 100;

/**
 * Câte linii primește o factură — `MANUAL_INVOICE_MAX_LINES` de pe server. Un an la un client cu
 * nouă servicii trece de ea (#210); spus sub linii, nu ca eroare de formă.
 */
export const INVOICE_MAX_LINES = 100;

/** Bifează sau debifează o lună; lista rămâne strânsă și în ordine. */
export function toggleMonth(months: readonly string[], month: string): string[] {
  return months.includes(month)
    ? months.filter((item) => item !== month)
    : [...months, month].sort();
}

let nextKey = 0;

function key(): string {
  nextKey += 1;
  return `linie-${nextKey}`;
}

/**
 * O linie liberă: un echipament, o bucată, **fără TVA** — liniile libere pornesc de la „fără TVA"
 * (#186), iar la firma neplătitoare alegerea nici nu apare.
 */
export function emptyLine(unitCode = 'H87'): LineDraft {
  return {
    key: key(),
    description: '',
    unitCode,
    quantity: '1',
    unitPrice: '',
    currency: 'RON',
    priceIncludesVat: false,
    // Serviciu până alege omul altfel: nu se ghicește din unitatea de măsură.
    isEquipment: false,
  };
}

/** Liniile precompletate, cu prețul scris cum îl tastează un român: `100,00`. */
export function fromPrefill(lines: readonly InvoiceLine[]): LineDraft[] {
  return lines.map((line) => ({
    key: key(),
    description: line.description,
    unitCode: line.unitCode,
    quantity: line.quantity.replace('.', ','),
    unitPrice: line.unitPrice.replace('.', ','),
    currency: line.currency,
    priceIncludesVat: line.priceIncludesVat,
    isEquipment: line.isEquipment,
  }));
}

/**
 * Cantitatea tastată → `"2.5"`, sau `null`. Virgulă sau punct, cel mult trei zecimale, **diferită
 * de zero**. Cu minus în față e o reducere (#208): `-1` × `10,00` scade 10 lei din factură.
 *
 * ⚠️ Minusul stă pe cantitate, nu pe preț: e-Factura nu primește preț unitar negativ (BR-27).
 * Se primește și `−` (U+2212), cum îl pun unele tastaturi și textele copiate dintr-un document.
 */
export function normalizeQuantity(value: string): string | null {
  const match = /^([-\u2212]?)(\d{1,7})(?:[.,](\d{1,3}))?$/.exec(value.trim());

  if (!match) {
    return null;
  }

  const sign = match[1] ? '-' : '';
  const integer = (match[2] ?? '0').replace(/^0+(?=\d)/, '');
  const fraction = (match[3] ?? '').replace(/0+$/, '');

  if (!/[1-9]/.test(integer + fraction)) {
    return null;
  }

  return `${sign}${fraction.length > 0 ? `${integer}.${fraction}` : integer}`;
}

/**
 * Formularul → cererea, sau erorile pe câmp. Nu trece nimic prin `number`: prețul se normalizează
 * pe șiruri (`normalizeAmount`), ca peste tot în aplicație.
 */
export function toRequest(
  form: InvoiceFormDraft,
  options: { readonly vatPayer: boolean },
): { readonly request: ManualInvoiceRequest } | { readonly errors: FormErrors } {
  const errors: Record<string, string> = {};
  const lines: InvoiceLine[] = [];

  if (form.lines.length === 0) {
    errors.lines = 'Factura are măcar o linie.';
  } else if (form.lines.length > INVOICE_MAX_LINES) {
    errors.lines = `Factura are ${form.lines.length} de linii; încap ${INVOICE_MAX_LINES}. Împarte lunile pe două facturi.`;
  }

  form.lines.forEach((line, index) => {
    const description = line.description.trim();
    const quantity = normalizeQuantity(line.quantity);
    const unitPrice = normalizeAmount(line.unitPrice);

    if (description.length === 0) {
      errors[`lines.${index}.description`] = 'Denumirea lipsește.';
    } else if (description.length > LINE_DESCRIPTION_MAX) {
      errors[`lines.${index}.description`] =
        `Denumirea are ${description.length} de caractere; încap ${LINE_DESCRIPTION_MAX}.`;
    }

    if (quantity === null) {
      errors[`lines.${index}.quantity`] =
        'Cantitatea: un număr diferit de zero, ca 2 sau 2,5; cu minus, ca -1, e o reducere.';
    }

    if (unitPrice === null) {
      errors[`lines.${index}.unitPrice`] = /^\s*[-\u2212]/.test(line.unitPrice)
        ? 'Prețul rămâne pozitiv; la o reducere, minusul se pune la cantitate: -1.'
        : 'Prețul: o sumă cu cel mult două zecimale, ca 82,64.';
    }

    if (quantity !== null && unitPrice !== null) {
      lines.push({
        description,
        unitCode: line.unitCode,
        quantity,
        unitPrice,
        currency: line.currency,
        // La neplătitoare alegerea nu apare: prețul e prețul.
        priceIncludesVat: options.vatPayer && line.priceIncludesVat,
        isEquipment: line.isEquipment,
      });
    }
  });

  if (
    form.kind === 'monthly' &&
    (form.months.length === 0 ||
      !form.months.every((month) => /^\d{4}-(0[1-9]|1[0-2])$/.test(month)))
  ) {
    errors.months = 'Alege măcar o lună facturată.';
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.issueDate)) {
    errors.issueDate = 'Alege data facturii.';
  }

  if (Object.keys(errors).length > 0) {
    return { errors };
  }

  return {
    request: {
      kind: form.kind,
      months: form.kind === 'monthly' ? [...new Set(form.months)].sort() : [],
      issueDate: form.issueDate,
      dueDate: form.dueDate.trim().length > 0 ? form.dueDate : null,
      lines,
    },
  };
}
